<template>
    <Drawer
        v-bind="$attrs"
        :visible="modelValue"
        :class="[
            'e-sidebar',
            sidebarBackgroundColor,
        ]"
        :position="position"
        :modal="overlay"
        :dismissable="closeOnPressingOutside"
        :close-on-escape="closeOnEscape"
        @update:visible="onUpdateVisible"
    >
        <template #container>
            <div class="tw:relative tw:h-full tw:w-full">
                <div class="tw:h-full tw:w-full tw:overflow-y-auto">
                    <slot />
                </div>
            </div>
        </template>
    </Drawer>
</template>

<script>
/**
 * ESidebar - Abstraction wrapper around PrimeVue's Drawer component.
 *
 *
 *   // Basic usage with v-model (works for both Buefy and PrimeVue)
 *   <e-sidebar v-model="isOpen">
 *     <div class="p-4">
 *       <h3>Sidebar Content</h3>
 *       <p>Additional information here</p>
 *     </div>
 *   </e-sidebar>
 *
 *   // With Buefy-specific props (will be ignored in PrimeVue mode if not compatible)
 *   <e-sidebar v-model="isOpen" type="is-light" :fullheight="true" :overlay="true">
 *     <div class="p-4">
 *       <h3>Buefy-styled Sidebar</h3>
 *       <p>This content is styled by Buefy.</p>
 *     </div>
 *   </e-sidebar>
 *
 */
import { getBgColorClass } from '@/components/ui/utils/componentMappings.js';

export default {
    name: 'ESidebar',

    inheritAttrs: false,

    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },

        right: {
            type: Boolean,
            default: false,
        },

        bottom: {
            type: Boolean,
            default: false,
        },

        fullheight: {
            type: Boolean,
            default: false,
        },

        fullwidth: {
            type: Boolean,
            default: false,
        },

        fullscreen: {
            type: Boolean,
            default: false,
        },

        type: {
            type: String,
            default: undefined,
        },

        overlay: {
            type: Boolean,
            default: false,
        },

        canCancel: {
            type: [Boolean, Array],
            default: () => ['escape', 'outside'],
        },

        onCancel: {
            type: Function,
            default: () => null,
        },
    },

    emits: ['update:modelValue', 'close'],

    computed: {
        isFullScreen() {
            return (
                this.fullscreen ||
                (this.fullheight && this.fullwidth) ||
                (this.$attrs.mobile === 'fullwidth' && this.isMobile)
            );
        },

        isMobile() {
            return this.$isMobile || this.$isTablet;
        },

        position() {
            if (this.isFullScreen) {
                return 'full';
            }
            if (this.right) {
                return 'right';
            }
            if (this.bottom) {
                return 'bottom';
            }
            return 'left';
        },

        sidebarBackgroundColor() {
            if (this.type) {
                return getBgColorClass(this.type);
            }
            return getBgColorClass();
        },

        closeOnPressingOutside() {
            if (typeof this.canCancel === 'boolean') {
                return this.canCancel;
            }
            if (Array.isArray(this.canCancel)) {
                return this.canCancel.includes('outside');
            }
            return true;
        },

        closeOnEscape() {
            if (typeof this.canCancel === 'boolean') {
                return this.canCancel;
            }
            if (Array.isArray(this.canCancel)) {
                return this.canCancel.includes('escape');
            }
            return true;
        },
    },

    methods: {
        onUpdateVisible(value) {
            if (!value) {
                this.$emit('close');
                this.onCancel();
            }

            this.$emit('update:modelValue', value);
        },
    },
};
</script>
